import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import ArticleCard from '@/components/base/ArticleCard';
import { useSeo } from '@/hooks/useSeo';
import { getCategory, getArticlesByCategory } from '@/utils/article';

const PAGE_SIZE = 6;
const SORTS = [
  { key: 'time', label: '最新发布' },
  { key: 'hot', label: '热门程度' },
];

export default function CategoryPage() {
  const { categoryName = '' } = useParams();
  const category = getCategory(categoryName);
  const [sub, setSub] = useState<string>('全部');
  const [sort, setSort] = useState('time');
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useSeo({
    title: category ? `${category.name}分类-黑料网曝大瓜` : '分类-黑料网曝大瓜',
    description: category
      ? `${category.name}分类：${category.desc}`
      : '浏览黑料网曝大瓜的娱乐八卦分类内容。',
    keywords: category ? `${category.name},明星八卦,娱乐爆料,黑料网曝大瓜` : '娱乐爆料,黑料网曝大瓜',
  });

  const all = useMemo(() => (category ? getArticlesByCategory(category.slug) : []), [category]);

  const filtered = useMemo(() => {
    let list = all;
    if (sub !== '全部') list = list.filter((a) => a.sub === sub);
    const sorted = [...list];
    if (sort === 'hot') sorted.sort((a, b) => b.views - a.views);
    else sorted.sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
    return sorted;
  }, [all, sub, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const resetFilters = () => {
    setSub('全部');
    setSort('time');
    setPage(1);
  };

  const changeSub = (v: string) => { setSub(v); setPage(1); };
  const changeSort = (v: string) => { setSort(v); setPage(1); };

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 py-24 text-center">
          <i className="ri-emotion-unhappy-line text-5xl text-foreground-300" />
          <h1 className="mt-4 font-heading font-bold text-2xl text-foreground-950">该分类不存在</h1>
          <p className="mt-2 text-sm text-foreground-500">你访问的分类可能已调整，去首页看看最新的大瓜吧。</p>
          <Link to="/" className="inline-flex mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            返回首页
          </Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main className="w-full max-w-[1280px] mx-auto px-4 md:px-8 pt-8">
        <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6">
          <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800">{category.name}</span>
        </nav>

        <div className="rounded-xl bg-background-100 border border-background-200 p-6 md:p-8">
          <div className="flex items-start gap-4">
            <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-2xl shrink-0">
              <i className={category.icon} />
            </span>
            <div>
              <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">{category.name}</h1>
              <p className="mt-2 text-sm text-foreground-600 max-w-2xl leading-relaxed">{category.desc}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 pt-8">
          {/* Filters */}
          <aside className="lg:col-span-1">
            <button
              type="button"
              onClick={() => setFilterOpen((v) => !v)}
              className="lg:hidden w-full flex items-center justify-between px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm font-medium text-foreground-800 cursor-pointer"
            >
              <span className="flex items-center gap-2"><i className="ri-filter-3-line" /> 筛选条件</span>
              <i className={`${filterOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-lg`} />
            </button>

            <div className={`${filterOpen ? 'block' : 'hidden'} lg:block mt-3 lg:mt-0 space-y-6`}>
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="font-heading font-semibold text-sm text-foreground-950 mb-3">子分类</h3>
                <div className="flex flex-wrap gap-2">
                  {['全部', ...category.subs].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => changeSub(s)}
                      className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer ${sub === s ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600'}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="font-heading font-semibold text-sm text-foreground-950 mb-3">排序方式</h3>
                <div className="flex flex-col gap-2">
                  {SORTS.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => changeSort(s.key)}
                      className={`flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors cursor-pointer ${sort === s.key ? 'bg-primary-50 text-primary-600 font-medium' : 'text-foreground-600 hover:bg-background-100'}`}
                    >
                      {s.label}
                      {sort === s.key && <i className="ri-check-line" />}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={resetFilters}
                className="w-full px-4 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
              >
                清空筛选
              </button>
            </div>
          </aside>

          {/* Content */}
          <div className="lg:col-span-3">
            <div className="flex items-center justify-between mb-5">
              <p className="text-sm text-foreground-500">
                共找到 <span className="text-primary-600 font-semibold">{filtered.length}</span> 篇相关内容
              </p>
              <div className="flex items-center gap-2">
                {SORTS.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => changeSort(s.key)}
                    className={`px-3 py-1.5 rounded-md text-xs transition-colors cursor-pointer ${sort === s.key ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:text-primary-600'}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {pageItems.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {pageItems.map((a) => (
                    <ArticleCard key={a.id} article={a} variant="grid" />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="mt-8 flex items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={safePage === 1}
                      className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line" />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setPage(n)}
                        className={`w-9 h-9 rounded-md text-sm transition-colors cursor-pointer ${n === safePage ? 'bg-primary-500 text-background-50 font-medium' : 'border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600'}`}
                      >
                        {n}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={safePage === totalPages}
                      className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line" />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="rounded-lg border border-dashed border-background-300 py-20 text-center">
                <i className="ri-inbox-line text-5xl text-foreground-300" />
                <p className="mt-4 text-sm text-foreground-500">暂无相关新闻</p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  清空筛选条件
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}